import IChartService from '../IChartService'
import {
  BarChartOptions,
  BarChartSeriesObject,
  ChartDataProperties,
  ChartOptionsType,
  PieChartOptions,
  TableChartOptions,
} from '@/models/chart.model'
import { WidgetProperties } from '@/models/dashboard.model'
import { assertUnreachable } from '@/utils/helpers'
import ColorConverterService from './ColorConverterService.impl'
import { AnalyzeTableItem } from '@/models/table.model'

export default class ChartService implements IChartService {
  constructor(protected colorConverterService: ColorConverterService) {}

  computeChartOptions(widget: WidgetProperties): ChartOptionsType {
    switch (widget.chartType) {
      case 'pie':
        return this.computePieChartOptions(widget)
      case 'bar':
        return this.computeBarChartOptions(widget)
      case 'table':
        return this.computeTableChartOptions(widget)
      default:
        return assertUnreachable(widget.chartType)
    }
  }

  protected computeTableChartOptions(widget: WidgetProperties): TableChartOptions {
    if (!widget.chart?.data) {
      throw new Error('Cannot create chart without data')
    }

    return {
      charttitle: widget.title,
      tooltip: {
        formatter: '{b} : {c} ({d}%)',
        trigger: 'item',
      },
      legend: {
        data: this.names(widget.chart.data),
        show: false,
        selected: this.legendSelected(widget.hiddenSeries),
      },
      color: this.colors(widget.chart.data),
      headers: [
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Artifacts', key: 'count', sortable: true },
      ],
      items: this.tableItems(widget.chart.data, widget.hiddenSeries),
      type: 'table',
    }
  }

  protected computePieChartOptions(widget: WidgetProperties): PieChartOptions {
    if (!widget.chart?.data) {
      throw new Error('Cannot create chart without data')
    }

    return {
      charttitle: widget.title,
      legend: {
        data: this.names(widget.chart.data),
        show: false,
        selected: this.legendSelected(widget.hiddenSeries),
      },
      tooltip: {
        formatter: '{b} : {c} ({d}%)',
        trigger: 'item',
      },
      color: this.colors(widget.chart.data),
      series: [
        {
          name: widget.title,
          type: 'pie',
          data: widget.chart.data
            .filter((item) => item.count !== 0)
            .map((item) => ({
              name: item.name,
              value: item.count,
            })),
          label: {
            show: widget.configuration?.showLabels ?? true,
          },
        },
      ],
      type: 'pie',
    }
  }

  protected computeBarChartOptions(widget: WidgetProperties): BarChartOptions {
    if (!widget.chart?.data) {
      throw new Error('Cannot create chart without data')
    }

    const chartData: BarChartSeriesObject[] = widget.chart.data.map((item) => ({
      type: 'bar',
      name: item.name,
      label: {
        show: widget.configuration?.showLabels ?? true,
        formatter: '{a}',
        rotate: 10,
        color: '#000',
        verticalAlign: 'bottom',
        position: 'bottom',
        offset: [-70, 30],
      },
      data: [
        {
          name: item.name,
          value: item.count,
          itemStyle: {
            color: this.colorConverterService.convertArtifactTypeColorToCssColor(
              item.color,
              item.name,
            ),
          },
        },
      ],
    }))

    return {
      charttitle: widget.title,
      legend: {
        data: this.names(widget.chart.data),
        show: false,
        selected: this.legendSelected(widget.hiddenSeries),
      },
      tooltip: {
        formatter: '{a} : {c}',
        trigger: 'item',
      },
      yAxis: {
        type: 'value',
      },
      xAxis: {
        data: [''],
        type: 'category',
      },
      series: chartData,
      type: 'bar',
    }
  }

  protected names(data: ChartDataProperties[]): string[] {
    return data.map((item) => item.name)
  }

  protected colors(data: ChartDataProperties[]): string[] {
    return data.map((item) =>
      this.colorConverterService.convertArtifactTypeColorToCssColor(item.color, item.name),
    )
  }

  protected legendSelected(hiddenSeries: string[]): Record<string, boolean> {
    return hiddenSeries.reduce((acc: Record<string, boolean>, curr) => {
      acc[curr] = false
      return acc
    }, {})
  }

  protected tableItems(
    data: ChartDataProperties[],
    hiddenSeries: string[],
  ): AnalyzeTableItem<number>[] {
    // Filter out items whose names are in the hiddenSeries list before mapping
    return data
      .filter((item) => !hiddenSeries.includes(item.name)) // Exclude items with names in hiddenSeries
      .map((item) => ({
        name: item.name,
        count: item.count,
      }))
  }
}
